import { Building } from 'store/building/types';
import { TimeRange } from 'store/resource-time-ranges/types';

export interface ResourceAPIResponse {
  data: ResourceResponse;
}

interface ResourceResponse {
  resource: Resource;
}

export interface ResourceState {
  resource: Resource;
  getResource: AsyncActionTypes;
  imageIsLoaded?: boolean;
  initialQueryString?: string;
}

export type FormResponseType = 'Text' | 'Number' | 'Yes/No';

export enum BookingWindowType {
  AVAILABLE = 'Available',
  UNAVAILABLE = 'Unavailable',
}

export interface BookingWindow {
  id: number;
  uuid: string;
  resource_id: number;
  rrule: string;
  buffer_before?: number;
  buffer_after?: number;
  buffer_between?: number;
  bookable_interval_close?: number;
  bookable_interval_open?: number;
  minimum_duration: number;
  maximum_duration: number;
  time_interval?: number;
  type: BookingWindowType;
  created_at: Date;
  updated_at: Date;
  deleted_at?: Date;
  pricing_rules?: Array<PricingRule>;
  cancellation_rules?: Array<CancellationRule>;
}

interface PricingRule {
  id: number;
  uuid: string;
  booking_window_id: number;
  name: string;
  min_interval_price: number;
  min_interval_duration_minutes: number;
  // Backend also sends payment_type (e.g. 'CREDITS', 'CURRENCY'); keep it optional for forward-compatibility.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  payment_type?: any;
  active_at: Date;
  expires_at: Date;
  created_at: Date;
  updated_at: Date;
  deleted_at: Date;
}

interface CancellationRule {
  id: number;
  uuid: string;
  booking_window_id: number;
  name?: string;
  description?: string;
  description_uuid?: string;
  cancellation_window_end_minutes: number;
  created_at: Date;
  updated_at: Date;
  deleted_at?: Date;
  allow_cancel_during?: boolean;
  allow_late_cancel?: boolean;
  cancel_during_fee_amount?: number;
  cancel_during_fee_percent?: number;
  late_cancel_fee_amount?: number;
  late_cancel_fee_percent?: number;
  cancellation_description?: string;
}

interface Address {
  formatted_address?: string;
}

export enum CTATypeEnum {
  URL = 'URL',
  EMAIL = 'EMAIL',
  PHONE = 'PHONE',
}

export enum DefaultTypeEnum {
  TV = 'TV',
  WIFI = 'WIFI',
  PHONE = 'PHONE',
}

export interface Feature {
  id: number;
  uuid: string;
  resource_id: number;
  default_type: DefaultTypeEnum;
  name: string;
  name_uuid?: string;
  description?: string;
  description_uuid?: string;
  icon?: string;
  cta_type?: CTATypeEnum;
  cta_value?: string;
  active?: boolean;
  created_at: Date;
  updated_at: Date;
  deleted_at: Date;
}

export interface LocationCoordinates {
  latitude: number;
  longitude: number;
}

export interface Location {
  id: string;
  name: string;
  address: string;
  default: boolean;
  city: string;
  coordinates: LocationCoordinates;
  logo_url: string;
}

export interface Resource {
  id: number;
  uuid: string;
  has_other_amenity?: boolean;
  address?: Address;
  timezone?: string;
  booking_windows: Array<BookingWindow>;
  created_at: Date | string;
  updated_at: Date | string;
  deleted_at?: Date;
  name: string;
  subtitle?: string;
  description?: string;
  type?: string;
  directions?: string;
  image_url?: string;
  capacity: string | number;
  manager_email: string;
  auto_confirm?: boolean;
  wifi?: boolean;
  tv_projector?: boolean;
  phone?: boolean;
  other_amenity?: string;
  other_amenity_icon?: string;
  other_amenity_url?: string;
  user_prompts?: Array<UserPrompt>;
  features?: Array<Feature>;
  available_now?: AvailableNow;
  minimum_price: number;
  add_ons?: Array<AddOn>;
  room_layouts?: Array<RoomLayout>;
  terms_and_conditions?: string;
  preset_booking_windows: string;
  show_free?: boolean;
  supports_multi_day_bookings?: boolean;
  enable_capacity_booking?: boolean;
  allowed_visitors?: number;
  closest_availability?: ClosestAvailability;
  building_uuid?: string;
  kiosk_booking_enabled?: boolean;
  buildings?: Array<Building>;
  location?: Location;
  discounts?: Array<Discount>;
  show_open_hours?: boolean;
  check_in?: boolean;
  bookable_period_from_now_in_hours?: number;
  integration?: string;
  is_favorite?: boolean;
}

export interface Discount {
  id: number;
  uuid: string;
  name: string;
  displayName: string;
  description: string;
  amount: number;
  daysApplied: Array<DaysOfWeek>;
  daysFormatted: string;
}

export enum DaysOfWeek {
  MONDAY = 'MONDAY',
  TUESDAY = 'TUESDAY',
  WEDNESDAY = 'WEDNESDAY',
  THURSDAY = 'THURSDAY',
  FRIDAY = 'FRIDAY',
  SATURDAY = 'SATURDAY',
  SUNDAY = 'SUNDAY',
}

export interface ClosestAvailability {
  start_at?: string;
  end_at?: string;
  closest_day_time_ranges: Array<TimeRange>;
}

export interface AvailableNow {
  available_minutes: number;
  start_at: string;
  end_at: string;
}

export enum CommonQuestionType {
  NUM_ATTENDEES = 'NUM_ATTENDEES',
  BOOKING_PURPOSE = 'BOOKING_PURPOSE',
  CATERING_INFO = 'CATERING_INFO',
  NEED_ASSISTANCE = 'NEED_ASSISTANCE',
  SPECIAL_REQUESTS = 'SPECIAL_REQUESTS',
}

export interface UserPrompt {
  id: number;
  uuid: string;
  resource_id: number;
  response_type: FormResponseType;
  prompt?: string;
  prompt_uuid?: string;
  common_type?: CommonQuestionType | null;
  is_required: boolean;
  created_at: Date | string;
  updated_at: Date | string;
  deleted_at?: Date | string;
}

export interface AddOn {
  id: string;
  name: string;
  price: number;
  description?: string;
  image_url?: string;
  /*
   * VIS-2193. The catalog product image's alt text. Optional and absent on old and third-party
   * add-ons: catalog-service validates image and alt text as a pair, so a product without an image
   * has neither. Render an empty `alt` when it is missing, because the product name is already
   * visible beside the image. Only the resource add-on carries it; selected add-ons and the booking
   * payload do not need it.
   */
  image_alt_text?: string;
  request_quantity: boolean;
  required: boolean;
  /*
   * RES-1049 widens this shape for native vendor add-ons, and RES-1159 adds one more. EVERY ONE of them
   * is OPTIONAL and must stay that way:
   * `AddOn` is a single shared interface and helix's prism.mapper.ts / nexudus.mapper.ts both construct it,
   * so a required field stops the third-party adapters compiling. Absent on every Prism/Nexudus add-on.
   *
   * currency     - ISO code for `price`. Prices are minor units (formatCurrency divides by 100).
   * owning_party - the catalog company uuid that owns the product. Lower-case before comparing or grouping:
   *                catalog_company_uuid is stored lower-cased and collates case-insensitively, while JS
   *                string identity does not, so a raw compare renders one party as two.
   * sort_order   - display order WITHIN an owning party's section. Not validated and not renumbered: gaps
   *                and duplicates are legal, and an all-zero resource is legal and common because omitting
   *                the field on write stores 0. Never treat it as a contiguous rank or an array index.
   */
  currency?: string;
  owning_party?: string;
  sort_order?: number;

  /*
   * RES-1049 adds these alongside the ones above, same rules: OPTIONAL, and absent on every
   * Prism and Nexudus add-on.
   *
   * owning_party_name - the owning company's display name, resolved server-side from `owning_party`.
   *                     catalog-service exposes no company-name field, so helix resolves it from the
   *                     company uuid; nothing else sources it. RES-1159 labels each party section
   *                     with this. Absent when the company does not resolve or has an empty name --
   *                     render the section without a heading rather than an empty one.
   * pricing_type      - catalog-service's pricing type: 'fixed' | 'quote_required' | 'free' | 'tiered'.
   *                     Needed to read `price` correctly. A quote-required product resolves NO price
   *                     from catalog-service, so it arrives as `price: 0` and is otherwise
   *                     indistinguishable from a genuinely free one. RES-1159 labels a quote-required
   *                     amount as an estimate rather than a charge.
   *                     RES-1241 added 'tiered': `price` is then the band at the smallest orderable
   *                     quantity, a from-price. Resolve against `price_tiers` for a chosen quantity.
   */
  owning_party_name?: string;
  pricing_type?: 'fixed' | 'quote_required' | 'free' | 'tiered';

  /*
   * VIS-2193 (helix#11201). The owning company's logo, already a full CDN url, beside
   * `owning_party_name`. Absent when the company has no logo or does not resolve; render the
   * initials disc instead. Same rules as the fields above: optional, absent on Prism and Nexudus.
   */
  owning_party_logo_url?: string;

  /*
   * RES-1241. The quantity bands behind a `tiered` product, ascending, already filtered by helix to the
   * currency above. Absent for every other pricing type.
   *
   * `price` on a tiered add-on is the band at the smallest orderable quantity - a from-price, not the
   * price of what the tenant selects. Resolve with `resolveTierPriceCents` rather than reading the
   * scalar, or the row states one figure and the total another.
   *
   * Not `AddOnQuantityRange`, which carries the same two boundaries in camelCase one level up and says
   * which quantities the vendor will accept, not which price applies. The casing keeps them apart.
   */
  price_tiers?: Array<AddOnPriceTier>;

  /*
   * Whether the owning party is the REQUEST BUILDING'S OWN landlord, so the step can order that
   * building's products first. Nothing else in the payload distinguishes a landlord-owned product
   * from a vendor-owned one.
   *
   * helix resolves this per building through taxonomy, NOT from the global `Company.is_landlord`
   * (which is true for a company that is a landlord at one building and a vendor at another).
   * Absent on every Prism and Nexudus add-on; `false` rather than absent for a native non-landlord.
   */
  owning_party_is_landlord?: boolean;

  /*
   * RES-1359, same rules again: optional, and absent on every Prism and Nexudus add-on.
   *
   * minimum_notice_seconds - present on every product carrying a lead time, orderable or not. What
   *                     the info line is built from.
   * unavailable_reason - the verdict, absent when the product can still be ordered. Greying a row on
   *                     the seconds instead kills every notice-bearing product.
   *
   * The SELECTION is filtered on the reason too, not just on menu membership: helix used to drop an
   * out-of-window product so a stale line cleared itself, and the id now stays. See `liveSelection`.
   *
   * RES-1398 makes that filter per line rather than blanket: a `minimum_notice` product the booking
   * already carries is kept and submitted. `price_unavailable` is never kept -- helix refuses it.
   */
  minimum_notice_seconds?: number;
  unavailable_reason?: 'minimum_notice' | 'price_unavailable';
  /* What one unit is: `each`, `hour`, `sq_ft`, `flat`, `day`, or an admin's own string. Never blank. */
  unit_type?: string;
}

/** One quantity band of a `tiered` add-on's price. See `AddOn.price_tiers`. */
export interface AddOnPriceTier {
  min_quantity: number;
  /** `null` on the last band only — catalog-service requires it to be open-ended. */
  max_quantity: number | null;
  /** Minor units, like `AddOn.price`. `formatCurrency` divides by 100. */
  price_cents: number;
}

export interface RoomLayout {
  id: string;
  name: string;
  description?: string;
  image_url?: string;
}

export interface RoomLayoutState {
  room_layout: RoomLayout;
}
